import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BiEnvelope, BiArrowBack, BiLoader } from 'react-icons/bi';
import Alert from '../components/Alert';
import { buildAuthUrl, getRedirectUrl } from '../utils/url';

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setMessageType('info');

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      setMessage('Password reset instructions have been sent to your email address. Please check your inbox and follow the instructions to reset your password.');
      setMessageType('success');
      setIsSubmitted(true);
    } catch (error: any) {
      setMessage('Demo password reset failed. This is a demo application.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 space-y-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
              <BiEnvelope className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Check Your Email</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-2">
              We've sent password reset instructions to your email address.
            </p>
          </div>

          {message && <Alert type={messageType} message={message} onClose={() => setMessage('')} />}

          <div className="text-center space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Didn't receive the email? Check your spam folder or try again.
            </p>

            <div className="space-y-2">
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setEmail('');
                  setMessage('');
                }}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <BiArrowBack className="mr-2" />
                Try Again
              </button>

              <Link
                to={buildAuthUrl('/auth/login', getRedirectUrl(searchParams))}
                className="block w-full text-center py-2 px-4 border border-slate-300 rounded-lg shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Forgot Password?</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2">
            Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>

        {message && <Alert type={messageType} message={message} onClose={() => setMessage('')} />}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" htmlFor="email">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <BiLoader className="animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <BiEnvelope className="mr-2" />
                  Send Reset Instructions
                </>
              )}
            </button>
          </div>
        </form>

        <div className="text-center text-sm text-slate-500 dark:text-slate-400">
          <p>
            Remember your password?{' '}
            <Link
              to={buildAuthUrl('/auth/login', getRedirectUrl(searchParams))}
              className="font-medium text-primary-600 hover:text-primary-500 flex items-center justify-center"
            >
              <BiArrowBack className="mr-1" />
              Back to Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 